import { useEffect, useRef, useState } from "react";
import { useWindowDimensions } from "react-native";
import { StyleSheet, useUnistyles } from "react-native-unistyles";
import { View, Text, Button } from "@src/components";
import { Icon } from "@src/components/Icon";
import { RightDrawer } from "@src/components/web/RightDrawer.web";
import { useSiteNames } from "@src/features/cameras/hooks/useSiteNames";
import { Banner } from "@visioni/a3s-ui/components";
import { describeExportFilters, filterSignature, useVehicleExport } from "@visioni/a3s-vehicle-tracker/app";
import {
  EXPORT_MAX_RANGE_DAYS,
  EXPORT_MAX_ROWS,
  isExportRangeAllowed,
} from "@visioni/a3s-vehicle-tracker/export/limits";
import type { ExportPanelProps } from "./export-panel-props";

const PANEL_WIDTH = 440;
const MAX_ROWS = EXPORT_MAX_ROWS.toLocaleString("en-AU");

const LIMITS_MESSAGE = [
  `Exports cover at most ${String(EXPORT_MAX_RANGE_DAYS)} days.`,
  `Exports include up to ${MAX_ROWS} sightings, in the current sort order (newest first by default). If more match, only the first ${MAX_ROWS} are included. Refine your search (shorter date range, a site, cameras or other filters) to export the rest.`,
].join("\n\n");

/** What the last export did, as the panel shows it. */
type Outcome =
  | { kind: "downloaded"; rowCount: number; truncated: boolean }
  | { kind: "range-too-large" }
  | { kind: "cancelled" }
  | { kind: "error"; message: string };

/**
 * Right-hand panel that explains what an export will include and its limits,
 * then fetches every matching sighting and downloads the `.xlsx`.
 *
 * Web only: the workbook is built in the browser and downloaded through a
 * temporary link. Native renders `ExportPanel.native.tsx` instead.
 */
export function ExportPanel({
  visible,
  onClose,
  filters,
  selectedSiteId,
  sort,
  timezone,
  cameras,
  camerasReady,
}: ExportPanelProps) {
  const { theme } = useUnistyles();
  const { width: windowWidth } = useWindowDimensions();
  const siteNames = useSiteNames();
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  // Bumped whenever a click's result stops being relevant (panel closed,
  // search changed), so a late result can't repopulate a reset panel.
  const runRef = useRef(0);
  const { exportWorkbook, isExporting, progress, cancel } = useVehicleExport({
    filters,
    selectedSiteId,
    sort,
    timezone,
    cameras,
  });

  // A result or a running export belongs to the search it was made for. When
  // the applied search changes, stop the export and drop the old result so the
  // panel never describes a file for different filters.
  const searchIdentity = `${selectedSiteId ?? ""}|${sort}|${timezone}|${filterSignature(filters)}`;
  useEffect(() => {
    runRef.current++;
    cancel();
    setOutcome(null);
  }, [searchIdentity, cancel]);

  const siteName = selectedSiteId ? (siteNames?.get(selectedSiteId) ?? selectedSiteId) : null;
  const summary = describeExportFilters({ filters, siteName, timezone, cameras });
  const rangeAllowed = isExportRangeAllowed(filters);

  const handleClose = () => {
    runRef.current++;
    cancel();
    setOutcome(null);
    onClose();
  };

  const handleDownload = async () => {
    const run = ++runRef.current;
    setOutcome(null);
    const result = await exportWorkbook();
    if (run !== runRef.current) return;
    switch (result.kind) {
      case "ok":
        saveFile(result.blob, result.fileName);
        setOutcome({ kind: "downloaded", rowCount: result.rowCount, truncated: result.truncated });
        return;
      case "cancelled":
        // Only a Cancel press gets here: closing the panel or changing the
        // search bumps the run first, so those results are dropped above.
        setOutcome({ kind: "cancelled" });
        return;
      default:
        setOutcome(result);
    }
  };

  const footer =
    isExporting ?
      <View flexDirection="row" alignItems="center" gap="md">
        <View flex={1}>
          <Button
            variant="primary"
            label="Download"
            loading
            loadingText={`Preparing… (${progress.fetched.toLocaleString("en-AU")} sightings)`}
            accessibilityLabel="Preparing the export"
          />
        </View>
        <Button variant="link" label="Cancel" accessibilityLabel="Cancel the export" onPress={cancel} />
      </View>
    : <View gap="sm">
        <Button
          variant="primary"
          label={outcome?.kind === "error" ? "Try again" : "Download"}
          accessibilityLabel={outcome?.kind === "error" ? "Try the export again" : "Download the export"}
          iconLeft={<Icon name="download" size={16} color={theme.colors.interactive.buttons.default_foreground} />}
          disabled={!rangeAllowed || !camerasReady}
          onPress={() => void handleDownload()}
        />
        {!camerasReady && (
          <Text textStyle="xs/regular" color="textMuted">
            Loading cameras…
          </Text>
        )}
      </View>;

  return (
    <RightDrawer
      visible={visible}
      onClose={handleClose}
      title="Export vehicle sightings"
      dialog
      width={Math.min(PANEL_WIDTH, windowWidth)}
      footer={footer}
    >
      <View gap="3xl">
        <View gap="md">
          <Text textStyle="sm/semibold" color="text">
            What will be exported
          </Text>
          <View style={styles.summary}>
            {summary.map((item) => (
              <View key={item.label} style={styles.summaryRow}>
                <Text textStyle="xs/regular" color="textMuted" style={styles.summaryLabel}>
                  {item.label}
                </Text>
                <Text textStyle="sm/regular" color="text" style={styles.summaryValue}>
                  {item.value}
                </Text>
              </View>
            ))}
          </View>
          <Text textStyle="xs/regular" color="textMuted">
            To change what&apos;s exported, close this panel and adjust your search.
          </Text>
        </View>

        <Banner variant="info" title="Export limits" message={LIMITS_MESSAGE} />

        {!rangeAllowed && (
          <Banner
            variant="warning"
            title="Date range too long"
            message={`Your selected date range is longer than ${String(EXPORT_MAX_RANGE_DAYS)} days. Narrow it to ${String(EXPORT_MAX_RANGE_DAYS)} days or less to export.`}
          />
        )}

        {outcome && <OutcomeMessage outcome={outcome} />}
      </View>
    </RightDrawer>
  );
}

function OutcomeMessage({ outcome }: { outcome: Outcome }) {
  switch (outcome.kind) {
    case "downloaded":
      return outcome.truncated ?
          <Banner
            variant="warning"
            title={`Downloaded the first ${outcome.rowCount.toLocaleString("en-AU")} ${outcome.rowCount === 1 ? "sighting" : "sightings"}`}
            message="More sightings match your search than one export can include. Refine your search to export the rest."
          />
        : <Text textStyle="sm/medium" color="text" accessibilityLiveRegion="polite">
            Downloaded {outcome.rowCount.toLocaleString("en-AU")} {outcome.rowCount === 1 ? "sighting" : "sightings"}.
          </Text>;
    case "range-too-large":
      // The Download button is disabled in this case; this only shows if the range changed mid-click.
      return (
        <Banner
          variant="warning"
          title="Date range too long"
          message={`Narrow the date range to ${String(EXPORT_MAX_RANGE_DAYS)} days or less to export.`}
        />
      );
    case "cancelled":
      return (
        <Text textStyle="sm/regular" color="textMuted">
          Export cancelled.
        </Text>
      );
    case "error":
      return (
        <View gap="xs" accessibilityLiveRegion="polite">
          <Text textStyle="sm/semibold" color="danger">
            The export failed
          </Text>
          <Text textStyle="sm/regular" color="textMuted">
            {outcome.message}
          </Text>
          <Text textStyle="sm/regular" color="textMuted">
            Try again, or refine your search if it keeps failing.
          </Text>
        </View>
      );
  }
}

/** Downloads `blob` as `fileName` through a temporary link. */
function saveFile(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  link.style.display = "none";
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Safari and Firefox can read the blob URL asynchronously after click()
  // returns, so revoking straight away can cancel the download. A minute is
  // ample for the browser to take the file, then the memory is released.
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

const styles = StyleSheet.create((theme) => ({
  summary: {
    borderWidth: 1,
    borderColor: theme.colors.borderCard,
    borderRadius: theme.spacing.md,
    overflow: "hidden",
  },
  summaryRow: {
    flexDirection: "row",
    gap: theme.spacing.lg,
    paddingHorizontal: theme.spacing.xl,
    paddingVertical: theme.spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.borderCard,
  },
  summaryLabel: {
    width: 110,
    paddingTop: 2,
  },
  summaryValue: {
    flex: 1,
  },
}));
